import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Theme } from "@radix-ui/themes";
import { Dialog as DialogRadix, VisuallyHidden } from "radix-ui";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { media } from "@themes/breakpoints";
import { theme } from "@themes/themes";

import { Dialog } from "../Dialog";

interface SheetProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showDragHandle?: boolean;
  fullScreenOnly?: boolean;
  "data-testid"?: string;
}

type SheetState = "min" | "full";

export const Sheet: React.FC<SheetProps> = ({
  children,
  open,
  onOpenChange,
  showDragHandle = false,
  fullScreenOnly = false,
  "data-testid": dataTestId,
}) => {
  const [sheetState, setSheetState] = useState<SheetState>(fullScreenOnly ? "full" : "min");
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ y: number; startTime: number } | null>(null);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [bottomInset, setBottomInset] = useState<number | null>(null);

  const container = document.getElementById("portal-root")!;

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!showDragHandle || fullScreenOnly) return;

    setIsDragging(true);
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setDragStart({ y: clientY, startTime: Date.now() });

    e.preventDefault();
  };

  const handleDragEnd = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !dragStart || fullScreenOnly) return;

      const clientY = "touches" in e ? e.changedTouches[0].clientY : e.clientY;
      const deltaY = clientY - dragStart.y;
      const dragDuration = Date.now() - dragStart.startTime;
      const velocity = Math.abs(deltaY) / dragDuration;

      const threshold = 50;
      const fastSwipe = velocity > 0.5 && dragDuration < 300;

      if (sheetState === "min") {
        if (deltaY < -threshold || (fastSwipe && deltaY < -30)) {
          setSheetState("full");
        } else if (deltaY > threshold || (fastSwipe && deltaY > 30)) {
          onOpenChange?.(false);
        }
      } else if (deltaY > threshold || (fastSwipe && deltaY > 30)) {
        setSheetState("min");
      }

      setIsDragging(false);
      setDragStart(null);
    },
    [isDragging, dragStart, sheetState, onOpenChange, fullScreenOnly],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
    };
    const handleMouseUp = (e: MouseEvent) => handleDragEnd(e);
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
    };
    const handleTouchEnd = (e: TouchEvent) => handleDragEnd(e);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, handleDragEnd]);

  useEffect(() => {
    if (!open) {
      setSheetState(fullScreenOnly ? "full" : "min");
    }
  }, [open, fullScreenOnly]);

  useEffect(() => {
    if (fullScreenOnly && sheetState !== "full") {
      setSheetState("full");
    }
  }, [fullScreenOnly, sheetState]);

  useEffect(() => {
    if (!open) {
      setViewportHeight(null);
      setMaxHeight(null);
      setBottomInset(null);
      return;
    }

    const vv = window.visualViewport;
    const getHeight = () => Math.round(vv?.height ?? window.innerHeight);
    const getBottomInset = () => {
      if (!vv) return 0;
      // iOS keyboard: visualViewport shrinks & shifts, but fixed bottom stays at layout viewport bottom.
      // Inset is the occluded area at the bottom.
      const occluded = window.innerHeight - (vv.height + vv.offsetTop);
      return Math.max(0, Math.round(occluded));
    };

    const update = () => {
      const h = getHeight();
      setViewportHeight(h);
      // Keep current sizing semantics (85vh desktop, 90vh mobile).
      const ratio = window.matchMedia(media.mobile.replace("@media ", "")).matches ? 0.9 : 0.85;
      setMaxHeight(Math.round(h * ratio));
      setBottomInset(getBottomInset());
    };

    update();

    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  return (
    <DialogRadix.Root open={open} onOpenChange={onOpenChange}>
      <DialogRadix.Portal container={container}>
        <Theme accentColor="indigo" appearance="dark">
          <Dialog.Overlay />
          <Content
            data-testid={dataTestId}
            data-sheet-state={fullScreenOnly ? "full" : sheetState}
            style={
              {
                ["--sheet-vh" as any]: viewportHeight ? `${viewportHeight}px` : undefined,
                ["--sheet-max-h" as any]: maxHeight ? `${maxHeight}px` : undefined,
                ["--sheet-bottom-inset" as any]: bottomInset ? `${bottomInset}px` : undefined,
              } as React.CSSProperties
            }
          >
            <VisuallyHidden.Root>
              <DialogRadix.Title>Sheet</DialogRadix.Title>
              <DialogRadix.Description>Sheet content</DialogRadix.Description>
            </VisuallyHidden.Root>
            {showDragHandle && !fullScreenOnly && (
              <DragHandle data-is-dragging={isDragging} onMouseDown={handleDragStart} onTouchStart={handleDragStart} />
            )}

            <Body data-sheet-state={fullScreenOnly ? "full" : sheetState}>{children}</Body>
          </Content>
        </Theme>
      </DialogRadix.Portal>
    </DialogRadix.Root>
  );
};

const contentShow = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(100%); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const Content = styled(DialogRadix.Content)`
  display: flex;
  flex-direction: column;
  position: fixed;
  bottom: var(--sheet-bottom-inset, 0px);
  left: 0;
  right: 0;

  width: 100%;
  height: auto;
  min-height: 16px;
  max-height: var(--sheet-max-h, 85vh);

  overflow: hidden;

  background-color: ${theme.color("gray-2")};

  border: 1px solid ${theme.color("accent-7")};
  border-bottom: 0;
  border-radius: 16px 16px 0 0;

  box-shadow: 0 -6px 36px rgba(0, 0, 0, 0.15);

  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  animation: ${contentShow} 300ms cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 1001;

  &:focus {
    outline: none;
  }

  &[data-sheet-state="full"] {
    top: 48px;
    bottom: var(--sheet-bottom-inset, 0px);
    height: auto;
    min-height: 0;
    max-height: none;
    border-radius: 0;
  }

  ${media.mobile} {
    border-radius: 12px 12px 0 0;
    max-height: var(--sheet-max-h, 90vh);

    &[data-sheet-state="full"] {
      border-radius: 0;
    }
  }
`;

export const DragHandle = styled.div`
  position: relative;

  width: 100%;
  height: 20px;

  cursor: grab;

  --handle-color: ${theme.color("gray-6")};

  &:hover {
    --handle-color: ${theme.color("accent-7")};
  }

  &:active {
    --handle-color: ${theme.color("accent-9")};
  }

  &[data-is-dragging="true"] {
    --handle-color: ${theme.color("accent-9")};
    cursor: grabbing;
  }

  &::before {
    content: "";
    position: absolute;

    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: 40px;
    height: 4px;

    background-color: var(--handle-color);

    border-radius: 2px;

    transition: all 0.2s ease;
  }

  &:active::before {
    width: 48px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  overflow-y: auto;
  overflow-x: hidden;

  -webkit-overflow-scrolling: touch;

  &[data-sheet-state="full"] {
    height: 100%;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.color("gray-6")};
    border-radius: 3px;

    &:hover {
      background: ${theme.color("gray-7")};
    }
  }
`;
